import { Box, Text, Group, Stack, Skeleton } from '@mantine/core'
import { ReactNode } from 'react'

interface DashboardCardProps {
  title: string
  value: string | number
  unit?: string
  subtitle?: string
  status?: 'success' | 'warning' | 'error'
  children?: ReactNode
  className?: string
  loading?: boolean
  icon?: ReactNode
}

const STATUS_COLORS = {
  success: '#2f9e44',
  warning: '#f59c1a',
  error: '#ff5b57',
} as const

export function DashboardCard({
  title,
  value,
  unit = '',
  subtitle,
  status = 'success',
  children,
  className = '',
  loading = false,
  icon,
}: DashboardCardProps) {
  const statusColor = STATUS_COLORS[status]

  if (loading) {
    return <Skeleton height={120} radius="md" />
  }

  return (
    <Box
      className={className}
      style={{
        backgroundColor: 'var(--ai-bg-elevated)',
        borderLeft: `4px solid ${statusColor}`,
        borderRadius: 'var(--mantine-radius-md)',
        padding: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        transition: 'all var(--ai-transition-base)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'
        e.currentTarget.style.transform = 'translateY(-2px)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)'
        e.currentTarget.style.transform = 'translateY(0)'
      }}
    >
      <Group justify="space-between" mb={12}>
        <Text size="sm" fw={600} c="dimmed">
          {title}
        </Text>
        {icon && (
          <Box
            style={{
              color: statusColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        )}
      </Group>

      <Group align="baseline" gap={6} mb={subtitle ? 8 : 0}>
        <Text size="xl" fw={700}>
          {value}
        </Text>
        {unit && (
          <Text size="sm" c="dimmed">
            {unit}
          </Text>
        )}
      </Group>

      {subtitle && (
        <Text size="xs" c="dimmed" mb={children ? 12 : 0}>
          {subtitle}
        </Text>
      )}

      {children && <Box>{children}</Box>}
    </Box>
  )
}
